import { MapObjectProperties } from "@iventis/map-types";
import { Listener } from "@iventis/types";
import { point } from "@turf/helpers";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import length from "@turf/length";
import { Feature, LineString } from "geojson";
import { splitLine } from "./split-line-geojson-helpers";

export enum SplitLineEvent {
    Start = "Start",
    Move = "Move",
    Cancel = "Cancel",
    AskSplit = "AskSplit",
    Split = "Split",
}

interface ListenerPattern {
    [SplitLineEvent.Move]: ((moveEvent: { lng: number; lat: number }, selectedMapObject: Feature<LineString, MapObjectProperties>) => void)[];
    [SplitLineEvent.Start]: (() => void)[];
    [SplitLineEvent.Cancel]: (() => void)[];
    [SplitLineEvent.AskSplit]: (() => void)[];
    [SplitLineEvent.Split]: ((features: Feature<LineString, MapObjectProperties>[], splitPoint: { lng: number; lat: number }) => void)[];
}

export class SplitLineHandler {
    private listeners: ListenerPattern = {
        [SplitLineEvent.Move]: [],
        [SplitLineEvent.Start]: [],
        [SplitLineEvent.Cancel]: [],
        [SplitLineEvent.AskSplit]: [],
        [SplitLineEvent.Split]: [],
    };

    private readonly onClickHandler: Listener;

    private readonly onMouseMoveHandler: Listener;

    private readonly onKeyDownHandler: Listener;

    private splitFeatures: Feature<LineString, MapObjectProperties>[] = [];

    private splitPoint: { lng: number; lat: number } | null = null;

    constructor(
        private selectedMapObject: Feature<LineString, MapObjectProperties>,
        mapEngineEvents: {
            onClick(callback: (event: { lng: number; lat: number }) => void): Listener;
            onMouseMove: (callback: (event: { lng: number; lat: number }) => void) => Listener;
            onKeyDown: (callback: (event: KeyboardEvent) => void) => Listener;
        }
    ) {
        this.onClickHandler = mapEngineEvents.onClick(this.handleOnClick.bind(this));
        this.onMouseMoveHandler = mapEngineEvents.onMouseMove(this.handleOnMouseMove.bind(this));
        this.onKeyDownHandler = mapEngineEvents.onKeyDown(this.handleOnKeyDown.bind(this));

        this.listeners[SplitLineEvent.Start].forEach((listener) => listener());
    }

    /** User has clicked the map to split the line */
    private handleOnClick(event: { lng: number; lat: number }) {
        const pointOnTheLine = this.getNearestPointOnTheLine(event);
        this.splitPoint = pointOnTheLine;
        this.splitFeatures = this.splitSelectedFeature(pointOnTheLine);
        this.listeners[SplitLineEvent.AskSplit].forEach((listener) => listener());
    }

    /** On mouse move, ensure we are emitting the point we are nearest to on the line */
    private handleOnMouseMove(event: { lng: number; lat: number }) {
        const nearestPoint = this.getNearestPointOnTheLine(event);
        const splitLine = this.splitSelectedFeature(nearestPoint);

        let start: number;
        let end: number;

        // Handle the nearestPoint is at the start/end of the line
        if (splitLine.length === 1) {
            const firstPointOfSelectedMapObject = this.selectedMapObject.geometry.coordinates[0];
            // Work out nearestPoint is at start of the line
            if (nearestPoint.lng === firstPointOfSelectedMapObject[0] && nearestPoint.lat === firstPointOfSelectedMapObject[1]) {
                start = 0;
                end = length(splitLine[0], { units: "meters" });
            } else {
                start = length(splitLine[0], { units: "meters" });
                end = 0;
            }
        } else {
            start = length(splitLine[0], { units: "meters" });
            end = length(splitLine[1], { units: "meters" });
        }

        this.listeners[SplitLineEvent.Move].forEach((listener) =>
            listener(nearestPoint, {
                ...this.selectedMapObject,
                properties: {
                    ...this.selectedMapObject.properties,
                    splitLine: { start, end },
                },
            })
        );
    }

    /** If key down is escape, want to cancel splitting the line */
    private handleOnKeyDown(event: KeyboardEvent) {
        if (event.key === "Escape") {
            this.listeners[SplitLineEvent.Cancel].forEach((listener) => listener());
        }
    }

    /** Get the nearest point on the line, if given lng and lat is more than thresholdToShowSplitIndicatorPx away return null  */
    private getNearestPointOnTheLine({ lng, lat }: { lng: number; lat: number } | undefined) {
        const pointOnTheLine = nearestPointOnLine(this.selectedMapObject, point([lng, lat]));
        const [lineLng, lineLat] = pointOnTheLine.geometry.coordinates;
        return { lng: lineLng, lat: lineLat };
    }

    private splitSelectedFeature(splitPoint: { lng: number; lat: number }) {
        // Split the line into two, at the point the user has clicked
        const lineFeatureCollection = splitLine(this.selectedMapObject, splitPoint);
        return lineFeatureCollection.features.map((f) => ({
            ...f,
            properties: {
                ...this.selectedMapObject.properties,
                id: crypto.randomUUID(),
            },
        }));
    }

    public confirmSplit() {
        this.listeners[SplitLineEvent.Split].forEach((listener) => listener(this.splitFeatures, this.splitPoint));
    }

    public on<E extends SplitLineEvent>(event: E, callback: ListenerPattern[keyof ListenerPattern][0]) {
        this.listeners[event].push(callback as () => void);
        return this;
    }

    public remove() {
        this.onClickHandler.remove();
        this.onMouseMoveHandler.remove();
        this.onKeyDownHandler.remove();
    }
}
